import { AxiosRequestConfig } from 'axios';

/**
 * The API base URLs whose auth failures we know how to interpret.
 *
 * Two jobs:
 *   1. Give the session triage logic a coarse "which backend answered" signal,
 *      because the same status means different things per backend (Core v1
 *      answers 404 for a dead token; appointments-api answers 401).
 *   2. Act as an allowlist. The auth interceptor is also installed on the shared
 *      `axios` default instance, which third-party code may use, so anything
 *      that does not resolve to a known host is ignored outright.
 *
 * Kept in its own module so `sessionMonitor` can read it without importing
 * `http/client`, which imports `sessionMonitor`.
 */
export const API_HOSTS = {
  /** Core v1/v2 via the auth gateway. Returns 404 for an unknown auth_token. */
  auth: process.env.REACT_APP_PUBLIC_AUTH_BASE_URL,
  /** Core v4 direct. Returns 401 for an unknown token. */
  core: process.env.REACT_APP_ENARA_URL,
  /** Core gateway used by the legacy billing endpoints. */
  coreGateway: process.env.REACT_APP_ENARA_BASE_URL,
  /** Returns 401 SESSION_INVALID for a dead token, 502/503 for Core trouble. */
  appointments: process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL,
  healthData: process.env.REACT_APP_PUBLIC_HEALTH_DATA_BASE_URL,
  billing: process.env.REACT_APP_PUBLIC_BILLING_API_BASE_URL,
  eligibility: process.env.REACT_APP_PUBLIC_ELIGIBILITY_BASE_URL,
  leads: process.env.REACT_APP_PUBLIC_LEADS_API_BASE_URL,
  onboarding: process.env.REACT_APP_PUBLIC_ONBOARDING_BASE_URL,
} as const;

export type ApiHostKey = keyof typeof API_HOSTS;

/**
 * Which known backend a request was aimed at, or null if we do not recognise it.
 *
 * Matches on the longest configured base URL so that overlapping prefixes (for
 * example `REACT_APP_ENARA_URL` and `REACT_APP_ENARA_BASE_URL`) resolve to the
 * more specific entry rather than whichever happens to be declared first.
 */
export const resolveApiHost = (config?: AxiosRequestConfig): ApiHostKey | null => {
  if (!config) {
    return null;
  }

  const absoluteUrl = `${config.baseURL ?? ''}${config.url ?? ''}`;
  if (!absoluteUrl) {
    return null;
  }

  let matchedKey: ApiHostKey | null = null;
  let matchedLength = -1;

  (Object.keys(API_HOSTS) as ApiHostKey[]).forEach((key) => {
    const base = API_HOSTS[key];
    if (!base || !absoluteUrl.startsWith(base)) {
      return;
    }
    if (base.length > matchedLength) {
      matchedKey = key;
      matchedLength = base.length;
    }
  });

  return matchedKey;
};
